import { useCallback, useEffect, useRef, useState } from "react";
import { fetchReportLayout, saveReportLayout, type ReportChartSpec } from "../api";

/**
 * Layout personal del reporte: qué se ve, en qué orden, y los gráficos guardados.
 *
 * Modelo de dos niveles, decidido a propósito sobre un lienzo plano: las secciones
 * se reordenan entre sí y los bloques dentro de su sección. Eso conserva el riel de
 * color que le dice al lector de qué fuente viene cada tabla — una tabla de Meta
 * flotando sobre las cards de GA4 sería más libre y menos legible.
 *
 * Persiste en `sys_config.dashboard_user_layouts` por usuario. La biblioteca de
 * gráficos viaja en el mismo documento: antes vivía en localStorage y se migra sola
 * la primera vez (ver `migrateLegacyCharts`).
 */

export const LAYOUT_KEY = "reporte-camilia";
const LEGACY_CHARTS_KEY = "wolf:reporte:graficos:v1";

export type SectionId = "ga4" | "gads" | "meta";

export interface SavedChart {
  id: string;
  title: string;
  spec: ReportChartSpec;
  createdAt: string;
}

export interface LayoutDoc {
  version: 1;
  /** Orden de las secciones entre sí. */
  sections: SectionId[];
  /** Orden de los bloques dentro de cada sección. */
  blocks: Record<string, string[]>;
  /** Orden de las cards dentro de cada grupo de tiles. */
  cards: Record<string, string[]>;
  /** Ids ocultos — bloques o cards. Ocultar nunca borra: se recupera desde la bandeja. */
  hidden: string[];
  charts: SavedChart[];
}

export const DEFAULT_LAYOUT: LayoutDoc = {
  version: 1,
  sections: ["ga4", "gads", "meta"],
  blocks: {
    ga4: ["ga4:overview", "ga4:funnel", "ga4:weekdays", "ga4:sources"],
    gads: ["gads:overview", "gads:alert", "gads:search", "gads:pmax", "gads:other", "gads:keywords", "gads:terms"],
    meta: ["meta:overview", "meta:alert", "meta:campaigns", "meta:ads"],
  },
  cards: {
    "ga4:overview": [
      "ga4:sesiones", "ga4:usuarios", "ga4:compras", "ga4:unavailable",
      "ga4:interacciones", "ga4:rebote",
      "ga4:articulos", "ga4:tasaConversion", "ga4:ticketPromedio", "ga4:ingresos",
    ],
    "gads:overview": [
      "gads:inversion", "gads:impresiones", "gads:clics", "gads:ctr", "gads:cpc", "gads:cpm",
      "gads:conversiones", "gads:campanas", "gads:cuotaImpresiones", "gads:rentabilidad",
    ],
    "meta:overview": [
      "meta:inversion", "meta:impresiones", "meta:clics", "meta:ctr", "meta:cpc", "meta:cpm",
      "meta:conversiones", "meta:campanas", "meta:rentabilidad",
    ],
  },
  hidden: [],
  charts: [],
};

function clone(doc: LayoutDoc): LayoutDoc {
  return JSON.parse(JSON.stringify(doc)) as LayoutDoc;
}

/**
 * Completa el documento guardado con lo que falte del default.
 *
 * Un layout viejo no conoce los bloques agregados después. Sin esta reconciliación,
 * cada bloque nuevo sería invisible para quien ya tenía layout guardado — que es la
 * forma clásica en que una feature nueva "no aparece" para los usuarios antiguos.
 */
export function reconcile(saved: Partial<LayoutDoc> | null | undefined): LayoutDoc {
  const base = clone(DEFAULT_LAYOUT);
  if (!saved) return base;

  const knownSections = base.sections;
  const savedSections = (saved.sections ?? []).filter((id): id is SectionId =>
    knownSections.includes(id as SectionId));
  base.sections = [...savedSections, ...knownSections.filter((id) => !savedSections.includes(id))];

  for (const [group, defaults] of Object.entries(DEFAULT_LAYOUT.blocks)) {
    const savedOrder = (saved.blocks?.[group] ?? []).filter((id) => defaults.includes(id));
    base.blocks[group] = [...savedOrder, ...defaults.filter((id) => !savedOrder.includes(id))];
  }
  for (const [group, defaults] of Object.entries(DEFAULT_LAYOUT.cards)) {
    const savedOrder = (saved.cards?.[group] ?? []).filter((id) => defaults.includes(id));
    base.cards[group] = [...savedOrder, ...defaults.filter((id) => !savedOrder.includes(id))];
  }

  base.hidden = Array.isArray(saved.hidden) ? saved.hidden.filter((id) => typeof id === "string") : [];
  base.charts = Array.isArray(saved.charts)
    ? saved.charts.filter((chart) =>
        Boolean(chart) && typeof chart.id === "string" && Array.isArray(chart.spec?.metrics))
    : [];
  return base;
}

/** Gráficos que quedaron en localStorage antes de que el layout viviera en BigQuery. */
function migrateLegacyCharts(): SavedChart[] {
  try {
    const raw = window.localStorage.getItem(LEGACY_CHARTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c) => c?.id && Array.isArray(c?.spec?.metrics)) : [];
  } catch {
    return [];
  }
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || to < 0 || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function newId(): string {
  return `chart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function sameSpec(a: ReportChartSpec, b: ReportChartSpec): boolean {
  return a.metrics.join(",") === b.metrics.join(",")
    && (a.dimension ?? "fecha") === (b.dimension ?? "fecha")
    && (a.days ?? 30) === (b.days ?? 30)
    && (a.platform ?? null) === (b.platform ?? null)
    && (a.chartType ?? "combo") === (b.chartType ?? "combo");
}

export function useReportLayout() {
  const [layout, setLayout] = useState<LayoutDoc>(DEFAULT_LAYOUT);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const saveTimer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchReportLayout(LAYOUT_KEY)
      .then((response) => {
        if (cancelled) return;
        const doc = reconcile(response.payload as Partial<LayoutDoc> | null);
        if (!response.payload) {
          const legacy = migrateLegacyCharts();
          if (legacy.length) doc.charts = legacy;
        }
        setLayout(doc);
      })
      .catch(() => { if (!cancelled) setLayout(DEFAULT_LAYOUT); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  /** Escribe con debounce: arrastrar produce muchos cambios seguidos y no hace
   *  falta un MERGE por cada milímetro. */
  const persist = useCallback((next: LayoutDoc) => {
    setLayout(next);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void saveReportLayout(LAYOUT_KEY, next).catch(() => undefined);
    }, 700);
  }, []);

  const update = useCallback((mutate: (doc: LayoutDoc) => void) => {
    setLayout((current) => {
      const next = clone(current);
      mutate(next);
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        void saveReportLayout(LAYOUT_KEY, next).catch(() => undefined);
      }, 700);
      return next;
    });
  }, []);

  const moveSection = useCallback((id: string, targetId: string) => update((doc) => {
    doc.sections = move(doc.sections, doc.sections.indexOf(id as SectionId),
      doc.sections.indexOf(targetId as SectionId)) as SectionId[];
  }), [update]);

  const moveBlock = useCallback((group: string, id: string, targetId: string) => update((doc) => {
    const list = doc.blocks[group] ?? [];
    doc.blocks[group] = move(list, list.indexOf(id), list.indexOf(targetId));
  }), [update]);

  const moveCard = useCallback((group: string, id: string, targetId: string) => update((doc) => {
    const list = doc.cards[group] ?? [];
    doc.cards[group] = move(list, list.indexOf(id), list.indexOf(targetId));
  }), [update]);

  const hide = useCallback((id: string) => update((doc) => {
    if (!doc.hidden.includes(id)) doc.hidden.push(id);
  }), [update]);

  const show = useCallback((id: string) => update((doc) => {
    doc.hidden = doc.hidden.filter((item) => item !== id);
  }), [update]);

  const resetLayout = useCallback(() => update((doc) => {
    const preserved = doc.charts;
    Object.assign(doc, clone(DEFAULT_LAYOUT));
    doc.charts = preserved;
  }), [update]);

  const addChart = useCallback((title: string, spec: ReportChartSpec): boolean => {
    if (layout.charts.some((chart) => sameSpec(chart.spec, spec))) return false;
    update((doc) => {
      doc.charts.push({ id: newId(), title, spec, createdAt: new Date().toISOString() });
    });
    return true;
  }, [layout.charts, update]);

  const removeChart = useCallback((id: string) => update((doc) => {
    doc.charts = doc.charts.filter((chart) => chart.id !== id);
  }), [update]);

  const moveChart = useCallback((id: string, targetId: string) => update((doc) => {
    doc.charts = move(doc.charts, doc.charts.findIndex((c) => c.id === id),
      doc.charts.findIndex((c) => c.id === targetId));
  }), [update]);

  const isHidden = useCallback((id: string) => layout.hidden.includes(id), [layout.hidden]);

  return {
    layout, loaded, editing, setEditing, persist,
    moveSection, moveBlock, moveCard, hide, show, isHidden, resetLayout,
    addChart, removeChart, moveChart,
  };
}
